<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import { StoreSettingsService } from '@/services/store-settings.services';
import ModuleToggleDrawer from './ModuleToggleDrawer.vue';

const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();

export interface IModuleDef {
  key: string;
  icon: string;
  labelKey: string;
  descKey: string;
}

const MODULES: IModuleDef[] = [
  {
    key: 'modules.click_and_collect',
    icon: 'pi-shopping-cart',
    labelKey: 'modulesMgmt.cnc',
    descKey: 'modulesMgmt.cncDesc',
  },
  {
    key: 'modules.pds_display',
    icon: 'pi-desktop',
    labelKey: 'modulesMgmt.pds',
    descKey: 'modulesMgmt.pdsDesc',
  },
  {
    key: 'modules.pos_register',
    icon: 'pi-calculator',
    labelKey: 'modulesMgmt.pos',
    descKey: 'modulesMgmt.posDesc',
  },
  {
    key: 'modules.kiosk',
    icon: 'pi-tablet',
    labelKey: 'modulesMgmt.kiosk',
    descKey: 'modulesMgmt.kioskDesc',
  },
];

// ── Liste commerces (paginée, filtrée, triée) ──────────────────────────────────
const ACTIVITIES = [
  'RESTAURANT',
  'FAST_FOOD',
  'BAKERY',
  'CAFE',
  'GROCERY',
  'SUPERMARKET',
  'BUTCHERY',
  'FISHMONGER',
  'PHARMACY',
  'FLORIST',
  'BOOKSTORE',
  'CLOTHING_STORE',
  'BEAUTY_STORE',
  'BARBERSHOP',
  'SALON',
  'ELECTRONICS_STORE',
  'HOME_DECOR',
  'HARDWARE_STORE',
  'PET_SHOP',
  'LIQUOR_STORE',
  'JEWELRY_STORE',
] as const;

const search = ref('');
const activityFilter = ref('');
const sortBy = ref<'created' | 'name'>('name');
const order = ref<'ASC' | 'DESC'>('ASC');
const page = ref(1);
const PAGE_SIZES = [10, 20, 50] as const;
const pageSize = ref<(typeof PAGE_SIZES)[number]>(20);

watch([search, activityFilter, sortBy, order, pageSize], () => {
  page.value = 1;
});

function toggleOrder() {
  order.value = order.value === 'ASC' ? 'DESC' : 'ASC';
}

// ── Dropdown activité (custom) ──────────────────────────────────────────────────
const activityDropdownOpen = ref(false);
function activityLabel(code: string) {
  return t(`modulesMgmt.activities.${code}`);
}
function selectActivity(code: string) {
  activityFilter.value = code;
  activityDropdownOpen.value = false;
}
function clearActivity() {
  activityFilter.value = '';
  activityDropdownOpen.value = false;
}

const {
  data: storesData,
  isPending: storesLoading,
  isError: storesError,
} = useQuery({
  queryKey: computed(() => [
    'mm-stores',
    page.value,
    pageSize.value,
    search.value,
    activityFilter.value,
    sortBy.value,
    order.value,
  ]),
  queryFn: async () => {
    const params = new URLSearchParams();
    params.append('pagination', 'true');
    params.append('page', String(page.value));
    params.append('pageSize', String(pageSize.value));
    params.append('sortBy', sortBy.value);
    params.append('order', order.value);
    if (search.value.trim()) params.append('designation', search.value.trim());
    if (activityFilter.value) params.append('activity', activityFilter.value);
    const res = await axiosInstance.get(
      `restaurants/search?${params.toString()}`,
    );
    return res.data;
  },
});
const stores = computed<any[]>(() => storesData.value?.data ?? []);
const storeTotal = computed(() => storesData.value?.total ?? 0);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(storeTotal.value / pageSize.value)),
);
const visiblePages = computed(() => {
  const total = totalPages.value;
  const cur = page.value;
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  const start = Math.max(1, Math.min(cur - 2, total - 4));
  return Array.from({ length: Math.min(5, total) }, (_, i) => start + i);
});

// ── Settings des commerces visibles (requêtes parallèles) ─────────────────────
const storeIds = computed(() => stores.value.map((s) => s.id));

const { data: settingsMap, isFetching: settingsLoading } = useQuery({
  queryKey: computed(() => ['mm-settings', storeIds.value]),
  queryFn: async () => {
    const results = await Promise.all(
      storeIds.value.map(async (id) => {
        try {
          const res = await StoreSettingsService.getStoreSettings(id);
          return [id, res.data?.data ?? res.data] as const;
        } catch {
          return [id, null] as const;
        }
      }),
    );
    return Object.fromEntries(results) as Record<string, any>;
  },
  enabled: computed(() => storeIds.value.length > 0),
});

function flatSettings(storeId: string): Record<string, unknown> {
  const view = settingsMap.value?.[storeId];
  if (!view?.categories) return {};
  const flat: Record<string, unknown> = {};
  for (const cat of view.categories) {
    for (const s of cat.settings ?? []) flat[s.key] = s.value;
  }
  return flat;
}
// Les 3 premiers modules (Click & Collect, PDS Display, Système de Caisse)
// sont forcés à "Activé" à l'affichage, quel que soit le réglage réel côté backend.
const FORCED_ON_MODULE_KEYS = [
  'modules.click_and_collect',
  'modules.pds_display',
  'modules.pos_register',
];
function moduleValue(storeId: string, key: string): boolean {
  if (FORCED_ON_MODULE_KEYS.includes(key)) return true;
  const v = flatSettings(storeId)[key];
  return v === true;
}

// ── Drawer toggle ────────────────────────────────────────────────────────────────
const drawerOpen = ref(false);
const drawerStore = ref<any | null>(null);
const drawerModule = ref<IModuleDef | null>(null);

function openToggleDrawer(store: any, mod: IModuleDef) {
  drawerStore.value = store;
  drawerModule.value = mod;
  drawerOpen.value = true;
}
function closeToggleDrawer() {
  drawerOpen.value = false;
}
async function onModuleToggled() {
  await queryClient.invalidateQueries({
    queryKey: ['mm-settings'],
    refetchType: 'active',
  });
}
</script>

<template>
  <div class="mm">
    <!-- Header -->
    <div class="mm__header">
      <div>
        <h1 class="mm__title">{{ t('modulesMgmt.title') }}</h1>
        <p class="mm__subtitle">{{ t('modulesMgmt.subtitle') }}</p>
      </div>
    </div>

    <!-- Filters -->
    <div class="mm__filters" @click="activityDropdownOpen = false">
      <div class="mm__search-wrap">
        <i class="pi pi-search mm__search-icon" />
        <input
          v-model="search"
          class="mm__search"
          :placeholder="t('modulesMgmt.searchPlaceholder')"
        />
      </div>

      <div class="mm__dropdown-wrap" @click.stop>
        <button
          class="mm__dropdown-btn"
          :class="{ 'mm__dropdown-btn--active': activityFilter }"
          @click="activityDropdownOpen = !activityDropdownOpen"
        >
          <i class="pi pi-tag" />
          <span>{{
            activityFilter
              ? activityLabel(activityFilter)
              : t('modulesMgmt.allActivities')
          }}</span>
          <i
            v-if="activityFilter"
            class="pi pi-times"
            @click.stop="clearActivity"
          />
          <i v-else class="pi pi-chevron-down" />
        </button>
        <Transition name="mm-drop">
          <div v-if="activityDropdownOpen" class="mm__dropdown-panel">
            <div class="mm__dropdown-list">
              <button
                class="mm__dropdown-item"
                :class="{ 'mm__dropdown-item--active': !activityFilter }"
                @click="clearActivity"
              >
                {{ t('modulesMgmt.allActivities') }}
              </button>
              <button
                v-for="a in ACTIVITIES"
                :key="a"
                class="mm__dropdown-item"
                :class="{ 'mm__dropdown-item--active': activityFilter === a }"
                @click="selectActivity(a)"
              >
                {{ activityLabel(a) }}
              </button>
            </div>
          </div>
        </Transition>
      </div>

      <select v-model="sortBy" class="mm__page-size-select">
        <option value="name">{{ t('modulesMgmt.sortByName') }}</option>
        <option value="created">{{ t('modulesMgmt.sortByDate') }}</option>
      </select>

      <button
        class="mm__sort-btn"
        :title="t('modulesMgmt.toggleOrder')"
        @click="toggleOrder"
      >
        <i
          :class="
            order === 'DESC' ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up'
          "
        />
      </button>

      <select v-model.number="pageSize" class="mm__page-size-select">
        <option v-for="ps in PAGE_SIZES" :key="ps" :value="ps">
          {{ ps }} {{ t('modulesMgmt.perPage') }}
        </option>
      </select>

      <span class="mm__count"
        >{{ storeTotal }} {{ t('modulesMgmt.results') }}</span
      >
    </div>

    <!-- Grid -->
    <div v-if="storesLoading" class="mm__grid">
      <div v-for="i in 4" :key="i" class="mm__card mm__card--sk" />
    </div>
    <div v-else-if="storesError" class="mm__empty">
      <i class="pi pi-exclamation-circle" />
      <p>{{ t('modulesMgmt.loadError') }}</p>
    </div>
    <div v-else-if="!stores.length" class="mm__empty">
      <i class="pi pi-objects-column" />
      <p>{{ t('modulesMgmt.empty') }}</p>
    </div>
    <div v-else class="mm__grid">
      <div v-for="store in stores" :key="store.id" class="mm__card">
        <div class="mm__card-hd">
          <div class="mm__card-logo">
            <img
              v-if="store.logoUrl"
              :src="store.logoUrl"
              :alt="store.designation"
              loading="lazy"
            />
            <i v-else class="pi pi-shop" />
          </div>
          <span class="mm__card-name">{{
            store.designation ?? store.name
          }}</span>
        </div>

        <div class="mm__mod-grid">
          <button
            v-for="mod in MODULES"
            :key="mod.key"
            class="mm__mod-item"
            :class="{ 'mm__mod-item--on': moduleValue(store.id, mod.key) }"
            :disabled="settingsLoading"
            @click="openToggleDrawer(store, mod)"
          >
            <i class="pi" :class="mod.icon" />
            <span class="mm__mod-item-label">{{ t(mod.labelKey) }}</span>
            <span
              class="mm__mod-badge"
              :class="
                moduleValue(store.id, mod.key)
                  ? 'mm__mod-badge--on'
                  : 'mm__mod-badge--off'
              "
            >
              {{
                moduleValue(store.id, mod.key)
                  ? t('modulesMgmt.on')
                  : t('modulesMgmt.off')
              }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="mm__pagination">
      <button class="mm__pg-btn" :disabled="page === 1" @click="page = 1">
        <i class="pi pi-angle-double-left" />
      </button>
      <button class="mm__pg-btn" :disabled="page === 1" @click="page--">
        <i class="pi pi-chevron-left" />
      </button>
      <button
        v-for="p in visiblePages"
        :key="p"
        class="mm__pg-btn"
        :class="{ 'mm__pg-btn--active': p === page }"
        @click="page = p"
      >
        {{ p }}
      </button>
      <button
        class="mm__pg-btn"
        :disabled="page === totalPages"
        @click="page++"
      >
        <i class="pi pi-chevron-right" />
      </button>
      <button
        class="mm__pg-btn"
        :disabled="page === totalPages"
        @click="page = totalPages"
      >
        <i class="pi pi-angle-double-right" />
      </button>
    </div>

    <ModuleToggleDrawer
      :open="drawerOpen"
      :store="drawerStore"
      :module="drawerModule"
      :current-value="
        drawerStore && drawerModule
          ? moduleValue(drawerStore.id, drawerModule.key)
          : false
      "
      @close="closeToggleDrawer"
      @toggled="onModuleToggled"
    />
  </div>
</template>

<style scoped>
.mm {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .mm {
    padding: 1.5rem;
  }
}

.mm__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}
.mm__title {
  margin: 0 0 0.15rem;
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--nxt-on-surface);
}
.mm__subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}

.mm__filters {
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.25rem;
  padding: 0.875rem 1rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.mm__search-wrap {
  position: relative;
  flex: 1;
  min-width: 12rem;
}
.mm__search-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.mm__search {
  width: 100%;
  height: 2.5rem;
  padding: 0 0.875rem 0 2.25rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
}
.mm__search:focus {
  border-color: #ffd60a;
}
.mm__page-size-select {
  height: 2.5rem;
  padding: 0 2rem 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}

.mm__sort-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  cursor: pointer;
  flex-shrink: 0;
}
.mm__sort-btn:hover {
  border-color: #ffd60a;
}

.mm__dropdown-wrap {
  position: relative;
}
.mm__dropdown-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.mm__dropdown-btn--active {
  border-color: #ffd60a;
  color: #0b1320;
}
.mm__dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 200;
  min-width: 16rem;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
.mm__dropdown-list {
  max-height: 260px;
  overflow-y: auto;
  padding: 0.375rem;
}
.mm__dropdown-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  cursor: pointer;
}
.mm__dropdown-item:hover {
  background: var(--nxt-surface-mid);
}
.mm__dropdown-item--active {
  background: var(--nxt-surface-mid);
  font-weight: 700;
  color: #0b1320;
}

.mm-drop-enter-active,
.mm-drop-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.mm-drop-enter-from,
.mm-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.mm__count {
  margin-left: auto;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  white-space: nowrap;
}

/* Grid & cards */
.mm__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.25rem;
}
.mm__card {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
}
.mm__card--sk {
  height: 18rem;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: mm-shimmer 1.4s infinite;
}
@keyframes mm-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.mm__card-hd {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.mm__card-logo {
  width: 2.75rem;
  height: 2.75rem;
  min-width: 2.75rem;
  border-radius: 50%;
  overflow: hidden;
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-on-surface-muted);
  font-size: 1.125rem;
}
.mm__card-logo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.mm__card-name {
  font-size: 1rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}

.mm__mod-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.625rem;
}
.mm__mod-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.375rem;
  padding: 0.75rem;
  border-radius: 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition:
    border-color 0.15s,
    background 0.15s;
}
.mm__mod-item:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.mm__mod-item i {
  font-size: 1rem;
  color: var(--nxt-on-surface-muted);
}
.mm__mod-item--on {
  border-color: rgba(16, 181, 16, 0.4);
  background: rgba(16, 181, 16, 0.06);
}
.mm__mod-item--on i {
  color: #0f7b0f;
}
.mm__mod-item-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.mm__mod-badge {
  font-size: 0.5625rem;
  font-weight: 800;
  padding: 0.1rem 0.4rem;
  border-radius: 0.3rem;
  letter-spacing: 0.04em;
}
.mm__mod-badge--on {
  background: rgba(16, 181, 16, 0.15);
  color: #0f7b0f;
}
.mm__mod-badge--off {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-muted);
}

.mm__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 4rem 1rem;
  color: var(--nxt-on-surface-muted);
}
.mm__empty i {
  font-size: 2rem;
  opacity: 0.4;
}

/* Pagination */
.mm__pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
}
.mm__pg-btn {
  min-width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.8125rem;
  font-family: inherit;
  cursor: pointer;
}
.mm__pg-btn--active {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
  font-weight: 700;
}
.mm__pg-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
</style>

<style>
[data-theme='dark'] .mm__filters,
[data-theme='dark'] .mm__card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .mm__search,
[data-theme='dark'] .mm__page-size-select,
[data-theme='dark'] .mm__sort-btn,
[data-theme='dark'] .mm__dropdown-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .mm__search:focus {
  border-color: #ffd60a;
}
[data-theme='dark'] .mm__dropdown-panel {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .mm__dropdown-item:hover,
[data-theme='dark'] .mm__dropdown-item--active {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .mm__card-logo {
  background: rgba(255, 255, 255, 0.05);
}
[data-theme='dark'] .mm__mod-item {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .mm__mod-item--on {
  background: rgba(16, 181, 16, 0.1);
  border-color: rgba(16, 181, 16, 0.35);
}
[data-theme='dark'] .mm__pg-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}
</style>
